<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { dynamicTime, shortTimestamp } from 'shared/helpers/timeHelper';

import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';

const props = defineProps({
  contacts: { type: Array, required: true },
  selectedContactIds: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['toggleContact', 'toggleAll']);

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const selectedIdsSet = computed(() => new Set(props.selectedContactIds || []));

const allSelected = computed(
  () =>
    props.contacts.length > 0 &&
    props.contacts.every(contact => selectedIdsSet.value.has(contact.id))
);

const someSelected = computed(
  () =>
    !allSelected.value &&
    props.contacts.some(contact => selectedIdsSet.value.has(contact.id))
);

const isSelected = id => selectedIdsSet.value.has(id);

const formatActivity = timestamp => {
  if (!timestamp) return '';
  return shortTimestamp(dynamicTime(timestamp));
};

const onClickViewDetails = async id => {
  const routeTypes = {
    contacts_dashboard_segments_index: ['contacts_edit_segment', 'segmentId'],
    contacts_dashboard_labels_index: ['contacts_edit_label', 'label'],
    contacts_dashboard_lists_index: ['contacts_edit_list', 'listId'],
  };
  const [name, paramKey] = routeTypes[route.name] || ['contacts_edit'];
  const params = {
    contactId: id,
    ...(paramKey && { [paramKey]: route.params[paramKey] }),
  };

  await router.push({ name, params, query: route.query });
};

const handleSelect = (id, value) => {
  emit('toggleContact', { id, value });
};

const handleToggleAll = event => {
  emit('toggleAll', event.target.checked);
};
</script>

<template>
  <table class="w-full text-sm border-separate border-spacing-0">
    <thead>
      <tr class="text-xs text-left text-n-slate-11">
        <th class="w-10 px-2 py-2 font-medium border-b border-n-weak">
          <Checkbox
            :model-value="allSelected"
            :indeterminate="someSelected"
            @change="handleToggleAll"
          />
        </th>
        <th class="px-2 py-2 font-medium border-b border-n-weak">
          {{ t('CONTACTS_LAYOUT.TABLE.NAME') }}
        </th>
        <th class="px-2 py-2 font-medium border-b border-n-weak">
          {{ t('CONTACTS_LAYOUT.TABLE.PHONE') }}
        </th>
        <th class="px-2 py-2 font-medium border-b border-n-weak">
          {{ t('CONTACTS_LAYOUT.TABLE.EMAIL') }}
        </th>
        <th
          class="px-2 py-2 font-medium text-right border-b border-n-weak whitespace-nowrap"
        >
          {{ t('CONTACTS_LAYOUT.TABLE.LAST_ACTIVITY') }}
        </th>
      </tr>
    </thead>
    <tbody>
      <tr
        v-for="contact in contacts"
        :key="contact.id"
        class="cursor-pointer hover:bg-n-alpha-2"
        :class="{ 'bg-n-alpha-2': isSelected(contact.id) }"
        @click="onClickViewDetails(contact.id)"
      >
        <td class="px-2 py-1.5 border-b border-n-weak" @click.stop>
          <Checkbox
            :model-value="isSelected(contact.id)"
            @change="event => handleSelect(contact.id, event.target.checked)"
          />
        </td>
        <td
          class="max-w-48 px-2 py-1.5 font-medium truncate text-n-slate-12 border-b border-n-weak"
        >
          {{ contact.name }}
        </td>
        <td
          class="max-w-40 px-2 py-1.5 truncate text-n-slate-11 border-b border-n-weak"
        >
          {{ contact.phoneNumber }}
        </td>
        <td
          class="max-w-56 px-2 py-1.5 truncate text-n-slate-11 border-b border-n-weak"
        >
          {{ contact.email }}
        </td>
        <td
          class="px-2 py-1.5 text-right whitespace-nowrap text-n-slate-11 border-b border-n-weak"
        >
          {{ formatActivity(contact.lastActivityAt) }}
        </td>
      </tr>
    </tbody>
  </table>
</template>
